import { api } from './client';

/**
 * The customer's own rides - the minimum this app needs to know one is live.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS SO SMALL, AND WHY IT EXISTS AT ALL
 * ---------------------------------------------------------------------------
 * A customer ride is a `customer_trip`: a standalone thing with no `bookingId`
 * hanging off it. That one fact is the whole reason this file is here. Every
 * other live activity in the app is reachable from a booking, so its screen
 * can carry the "call your professional" affordance - a ride has no booking
 * and therefore no detail screen to put one on.
 *
 * So the Bookings tab becomes the app's live-activity surface, and this is
 * what tells it there is a driver to show. Nothing else is modelled: no fare,
 * no route, no vehicle. The moment this app grows a real ride screen, that
 * screen owns the ride type and this shrinks back to a single fetch.
 */

/**
 * The statuses a ride passes through, as the platform names them.
 *
 * Listed rather than typed as `string` so `LIVE_RIDE_STATUSES` below is
 * checkable: a status renamed server-side becomes a compile error here rather
 * than a driver card that silently stops appearing.
 */
export type RideStatus =
  | 'requested'
  | 'assigning'
  | 'assigned'
  | 'en_route'
  | 'arrived'
  | 'started'
  | 'completed'
  | 'cancelled'
  | 'unassignable';

/**
 * A ride with somebody actually driving it.
 *
 * These four are exactly the set in which `riderId` is non-null, which is why
 * they are also exactly the set the server will open a voice session for. Ask
 * for a call outside them and eligibility answers `NOT_YET_ASSIGNED`.
 */
export const ASSIGNED_RIDE_STATUSES: RideStatus[] = [
  'assigned',
  'en_route',
  'arrived',
  'started',
];

/**
 * A ride the customer still has a stake in.
 *
 * Wider than `ASSIGNED_RIDE_STATUSES` on purpose, and the difference matters:
 * `requested` and `assigning` are the first thing that happens after somebody
 * taps Confirm. Filtering them out - which this file used to do - meant a
 * customer booked a driver, was returned to the Bookings tab, and found
 * nothing there at all until dispatch happened to land. "Did that work?" is
 * the worst question an app can leave somebody asking.
 *
 * There is genuinely nobody to call in those two states, and the card says so
 * rather than offering a button that would fail. But there IS something
 * honest to show: we are looking for your driver.
 *
 * `unassignable`, `completed` and `cancelled` stay out - those are endings,
 * and an ending belongs in history rather than pinned to the top of the tab.
 */
const ACTIVE_RIDE_STATUSES: RideStatus[] = [
  'requested',
  'assigning',
  ...ASSIGNED_RIDE_STATUSES,
];

/**
 * The vehicle, frozen onto the ride when a driver was assigned.
 *
 * A snapshot rather than a pointer, server-side, so a driver who changes
 * vehicle tomorrow does not rewrite a trip that already happened. Every field
 * is nullable because a rider's profile may be incomplete - the registration
 * number is the one that matters at the kerb, and the card degrades to
 * whichever parts exist.
 */
export type RideVehicle = {
  riderName: string | null;
  vehicleType: string | null;
  make: string | null;
  model: string | null;
  colour: string | null;
  registrationNumber: string | null;
};

/** One ride, as `GET /customers/me/rides` returns it. */
type ApiRide = {
  id: string;
  rideNumber?: string;
  status: string;
  riderId?: string | null;
  pickupAddressText?: string | null;
  dropAddressText?: string | null;
  estimatedMinutes?: number | null;
  estimatedDistanceKm?: string | number | null;
  scheduledPickupAt?: string | null;
  /**
   * Shown to the requester only, and only while the ride is `arrived`. The
   * driver reads it off this screen - it is what proves the two of them have
   * actually met before the trip starts.
   */
  startOtpCode?: string | null;
  vehicleSnapshot?: Partial<RideVehicle> | null;
  createdAt?: string;
  assignedAt?: string | null;
  arrivedAt?: string | null;
  startedAt?: string | null;
};

export type Ride = {
  id: string;
  rideNumber: string | null;
  status: RideStatus;
  /** Null until a driver is assigned. */
  riderId: string | null;
  pickupAddressText: string | null;
  dropAddressText: string | null;
  /** The platform's own estimate, in minutes. Null before one is computed. */
  estimatedMinutes: number | null;
  estimatedDistanceKm: number | null;
  /** Null on a trip wanted now. */
  scheduledPickupAt: string | null;
  startOtpCode: string | null;
  vehicle: RideVehicle | null;
  createdAt: string | null;
  assignedAt: string | null;
  arrivedAt: string | null;
  startedAt: string | null;
};

/**
 * `estimatedDistanceKm` is a Prisma `Decimal`, which serialises as a STRING.
 * Reading it as a number without this returns NaN and renders "NaN km".
 */
const toNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toVehicle = (
  snapshot: Partial<RideVehicle> | null | undefined,
): RideVehicle | null => {
  if (!snapshot || typeof snapshot !== 'object') return null;
  return {
    riderName: snapshot.riderName ?? null,
    vehicleType: snapshot.vehicleType ?? null,
    make: snapshot.make ?? null,
    model: snapshot.model ?? null,
    colour: snapshot.colour ?? null,
    registrationNumber: snapshot.registrationNumber ?? null,
  };
};

const toRide = (row: ApiRide): Ride => ({
  id: row.id,
  rideNumber: row.rideNumber ?? null,
  status: row.status as RideStatus,
  riderId: row.riderId ?? null,
  pickupAddressText: row.pickupAddressText ?? null,
  dropAddressText: row.dropAddressText ?? null,
  estimatedMinutes: row.estimatedMinutes ?? null,
  estimatedDistanceKm: toNumber(row.estimatedDistanceKm),
  scheduledPickupAt: row.scheduledPickupAt ?? null,
  startOtpCode: row.startOtpCode ?? null,
  vehicle: toVehicle(row.vehicleSnapshot),
  createdAt: row.createdAt ?? null,
  assignedAt: row.assignedAt ?? null,
  arrivedAt: row.arrivedAt ?? null,
  startedAt: row.startedAt ?? null,
});

/**
 * Every ride this customer has.
 *
 * Typed against both shapes the endpoint can answer in. A bare array is what
 * the contract says; a `{ rides: [...] }` envelope is what list endpoints
 * drift into once they grow paging, and tolerating it here costs one line
 * rather than a release where the driver card quietly never appears.
 */
export const fetchRides = async (signal?: AbortSignal): Promise<Ride[]> => {
  const payload = await api.get<ApiRide[] | { rides?: ApiRide[] } | null>(
    '/customers/me/rides',
    { signal },
  );

  const rows = Array.isArray(payload) ? payload : payload?.rides ?? [];
  return rows.map(toRide);
};

/**
 * The one ride happening right now, or null.
 *
 * There is at most one by construction - a customer cannot be in two cars -
 * so this answers with a ride rather than a list, and the card that reads it
 * needs no "which one" logic.
 */
export const fetchLiveRide = async (
  signal?: AbortSignal,
): Promise<Ride | null> => {
  const rides = await fetchRides(signal);
  return rides.find(ride => ACTIVE_RIDE_STATUSES.includes(ride.status)) ?? null;
};

/**
 * One end of a trip.
 *
 * Coordinates are what the platform routes on; the text is what a person
 * reads on the driver's screen and in their own history. The text is optional
 * because a pin dropped on a map may have no address the geocoder will admit
 * to, and refusing the booking over that would be worse than a trip labelled
 * by its coordinates.
 */
export type RidePoint = {
  lat: number;
  lng: number;
  addressText?: string;
};

/**
 * Order a driver.
 *
 * Returns as soon as the ride is queued - a driver is chosen a moment later,
 * not while this call is open, so the caller gets a ride in `requested` and
 * learns who is coming by polling it. `fetchLiveRide` is that poll, and the
 * driver card on the Bookings tab is what reads it.
 *
 * **No fare anywhere in this call.** Pricing for rides is not implemented
 * server-side, so there is nothing to quote, nothing to confirm and nothing to
 * charge. The screens that call this deliberately show no money rather than a
 * number that would be a guess - see `DriverScreen`.
 *
 * `scheduledPickupAt` omitted means now. Sent, it is the deadline the driver
 * must be at the pickup by, which is a different promise from "the trip starts
 * then" and is what the platform's own scheduler works back from.
 */
export const requestRide = (input: {
  pickup: RidePoint;
  drop: RidePoint;
  scheduledPickupAt?: Date;
}): Promise<Ride> =>
  api
    .post<ApiRide>('/customers/me/rides', {
      pickupLat: input.pickup.lat,
      pickupLng: input.pickup.lng,
      ...(input.pickup.addressText
        ? { pickupAddressText: input.pickup.addressText }
        : {}),
      dropLat: input.drop.lat,
      dropLng: input.drop.lng,
      ...(input.drop.addressText
        ? { dropAddressText: input.drop.addressText }
        : {}),
      ...(input.scheduledPickupAt
        ? { scheduledPickupAt: input.scheduledPickupAt.toISOString() }
        : {}),
    })
    .then(toRide);

/**
 * Call off a ride.
 *
 * Available right up to the moment it starts; after that only ops can stop it,
 * because somebody is in the car.
 *
 * `reason` is **required by the API** - `CancelRideDto` validates it at three
 * characters or more - so it is required here too rather than defaulted
 * silently. A caller with no reason to offer should pass a real one on the
 * customer's behalf ("Cancelled from the app"), not an empty string that the
 * server will refuse with a validation error nobody can act on.
 */
export const cancelRide = (rideId: string, reason: string): Promise<Ride> =>
  api
    .post<ApiRide>(`/customers/me/rides/${rideId}/cancel`, { reason })
    .then(toRide);
